import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar, Users, Trophy, Store, Wifi, ExternalLink, EyeOff, Flag } from "lucide-react"
import {
  TournamentStatusBadge,
  TournamentPresentialBadge,
} from "@/components/ui/TournamentStatusBadge"
import { CountryFlag } from "./ui/country-flag"
import { Tournament, useCountries } from "@/graphql"
import { toast } from "@/hooks/use-toast"
import { useAuthContext } from "@/contexts/AuthContext"
import { getTournamentImage } from "@/pages/Tournament/Detail/utils"
import { useTournamentFinish } from "@/graphql/hooks/useTournamentFinish"

interface TournamentCardProps {
  tournament: Tournament
  glowColor?: "red" | "green" | "purple" | "yellow"
}

export const TournamentCard = ({ tournament }: TournamentCardProps) => {
  const { isAdmin } = useAuthContext()
  const { countriesMap } = useCountries()
  const { tournamentFinish, loading: finishLoading } = useTournamentFinish()

  const handleFinishTournament = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const result = await tournamentFinish({ tournamentId: tournament.id })
    if (result?.status?.success) {
      toast({
        title: "Torneo reportado",
        description: `El torneo ${tournament.name} ha sido reportado correctamente.`,
        variant: "success",
      })
    }
  }

  // Mapeo de colores a valores CSS para glow
  const colorMap = {
    "Furia Extendido": "255, 0, 0", // rojo
    "Primer Bloque": "255, 255, 0", // amarillo
    "Primera Era": "128, 0, 255", // morado
    Imperio: "0, 200, 255", // azul/celeste
  }
  const glowColor = colorMap[tournament.Game.name]

  const handleOpenTournament = () => {
    if (tournament.isPrivate && !isAdmin) {
      toast({
        title: "Torneo Privado",
        description:
          "No tienes permiso para ver los detalles de este torneo. Una vez terminado el torneo será público en el sistema.",
        variant: "destructive",
      })
      return
    }

    if (tournament.urlView) {
      window.open(tournament.urlView, "_blank", "noopener,noreferrer")
    }
  }

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handleOpenTournament()
    }
  }

  return (
    <div className="relative group">
      <div
        className="absolute inset-0 rounded-lg z-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          backgroundColor: `rgba(${glowColor}, 0.5)`, // color del glow
          filter: "blur(24px)",
          pointerEvents: "none",
        }}
      />

      <Card
        className="relative z-10 overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-[1.06] 
                   bg-gradient-card dark:bg-gradient-card-dark 
                   border-border dark:border-gray-700"
        onClick={handleOpenTournament}
        onKeyDown={handleCardKeyDown}
        role="link"
        tabIndex={0}
      >
        <div className="relative h-48 overflow-hidden rounded-t-lg">
          <TournamentStatusBadge
            statusId={tournament.statusId}
            className="absolute top-3 right-3 z-20 scale-110"
          />
          <TournamentPresentialBadge
            state={!!tournament.isPresential}
            className="absolute top-3 left-3 z-20"
          />
          <img
            src={getTournamentImage(tournament)}
            alt={tournament.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none"
            }}
          />
          {tournament.Store?.name && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 z-10">
              <p className="flex items-center text-white text-sm font-medium">
                <Store className="inline-block h-4 w-4 mr-2" />
                {tournament.Store.name}
              </p>
            </div>
          )}
        </div>

        <CardHeader className="p-3">
          <h3 className="font-semibold text-lg leading-tight line-clamp-2">{tournament.name}</h3>
          {tournament.Game && (
            <p className="text-sm text-muted-foreground">{tournament.Game.name}</p>
          )}
        </CardHeader>

        <CardContent className="space-y-3 px-3 pb-3">
          {tournament.description && (
            <p className="text-sm text-muted-foreground dark:text-gray-300 line-clamp-4">
              {tournament.description}
            </p>
          )}

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground dark:text-gray-400" />
              <span>
                {new Date(tournament.startDate).toLocaleDateString("es-ES", {
                  weekday: "long",
                  year: "numeric",
                  month: "2-digit",
                  day: "numeric",
                })}
              </span>
            </div>
            {tournament.maxPlayers && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground dark:text-gray-400" />
                <span>{tournament.maxPlayers} jugadores</span>
              </div>
            )}
            {tournament.rounds && (
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-muted-foreground dark:text-gray-400" />
                <span>{tournament.rounds} rondas</span>
              </div>
            )}
            {tournament.Address?.countryId && (
              <div className="flex items-center gap-2">
                <CountryFlag countryCode={countriesMap.get(tournament.Address.countryId).iso} />
                <span>{countriesMap.get(tournament.Address.countryId).name}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            {tournament.Game && (
              <Badge className="dark:bg-gray-700 dark:text-gray-100">{tournament.Game.name}</Badge>
            )}
            {tournament.Format && (
              <Badge variant="secondary" className="dark:bg-gray-600 dark:text-gray-100">
                {tournament.Format.name}
              </Badge>
            )}
            {tournament.TournamentType && (
              <Badge variant="outline" className="dark:border-gray-500 dark:text-gray-100">
                {tournament.TournamentType.name}
              </Badge>
            )}
            {!tournament.isPresential && (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
              >
                <Wifi className="inline w-4 h-4 mr-2" /> Online
              </Badge>
            )}
            {tournament.isPrivate && (
              <Badge variant="destructive" className="">
                <EyeOff className="inline w-4 h-4 mr-2" /> Torneo Privado
              </Badge>
            )}
          </div>
        </CardContent>
        <CardFooter className="p-3 pt-0 flex gap-2">
          {tournament.urlView && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
              onClick={(e) => {
                e.stopPropagation()
                handleOpenTournament()
              }}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ver Detalles
            </Button>
          )}
          {isAdmin && tournament.statusId === 5 && (
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              onClick={handleFinishTournament}
              disabled={finishLoading}
            >
              <Flag className="w-4 h-4 mr-2" />
              {finishLoading ? "Finalizando..." : "Finalizar"}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
